import { NOTES_TEXTAREA_SX } from '@/constants/app/forms';
import { useUpsertCaseMutation } from '@/store/api/services/operations/cases/cases';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, ButtonGroup, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

const CloseCaseSchema = z.object({
  wasSolved: z.boolean(),
  reason: z.string().min(1, 'Reason is required'),
});

type CloseCaseFormData = z.infer<typeof CloseCaseSchema>;

interface CaseCloseModalProps extends ModalChildrenProps {
  caseId: string;
}

const CaseCloseModal = ({ hide, props }: CaseCloseModalProps) => {
  const [upsertCase, { isLoading: isSubmitting }] = useUpsertCaseMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CloseCaseFormData>({
    resolver: zodResolver(CloseCaseSchema),
    defaultValues: {
      wasSolved: true,
      reason: '',
    },
  });

  const onSubmit = async (formData: CloseCaseFormData) => {
    if (!props?.caseId) {
      toast.error('Case ID is required');
      return;
    }

    try {
      // Always include the reason/resolution_summary regardless of status
      const payload: any = {
        id: props.caseId,
        status: formData.wasSolved ? 'resolved' : 'closed',
        resolution_summary: formData.reason,
      };

      await upsertCase(payload).unwrap();

      toast.success(formData.wasSolved ? 'Case resolved successfully' : 'Case closed successfully');
      hide();
      reset();
    } catch (error: any) {
      const errorMessage =
        error?.meta?.message || error?.data?.message || error?.message || 'Failed to close case';
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    hide();
  };

  const wasSolved = watch('wasSolved');

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
        {wasSolved ? 'Resolution details' : 'Closing details'}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Was the case solved?
        </Typography>
        <Controller
          name="wasSolved"
          control={control}
          render={({ field }) => (
            <ButtonGroup>
              <Button
                variant={field.value ? 'contained' : 'outlined'}
                onClick={() => field.onChange(true)}
              >
                Yes
              </Button>
              <Button
                variant={!field.value ? 'contained' : 'outlined'}
                onClick={() => field.onChange(false)}
              >
                No
              </Button>
            </ButtonGroup>
          )}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {wasSolved ? 'Resolution summary' : 'Closing reason'}
        </Typography>
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              multiline
              rows={4}
              fullWidth
              placeholder={
                wasSolved
                  ? 'Provide a detailed resolution summary...'
                  : 'Provide a reason for closing this case...'
              }
              error={!!errors.reason}
              helperText={errors.reason?.message}
              sx={NOTES_TEXTAREA_SX}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>Back</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : wasSolved ? 'Resolve Case' : 'Close Case'}
        </Button>
      </Box>
    </Box>
  );
};

export default CaseCloseModal;
